import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from "../../components/ui/card";
import { useInitialVisitStore } from "../../stores/initialVisitStore";
import { Button } from "../../components/ui/button";
import { DateTime } from "luxon";
import { useInitialVisitDetails } from "../../hooks/useInitialVisitDetails";
import { useCallback, useEffect, useState } from "react";
import { CircleAlertIcon, Loader2 } from "lucide-react";
import { Calendar } from "../../components/ui/calendar";
import axios from "axios";
import { toast } from "sonner";
import { useWaitlistAvailability } from "../../hooks/useWaitlistAvailability";

export function WaitlistSelection() {
  const {
    uniqueId,
    setWaitlist,
    setDate,
    setVisibleMonth,
    bookingStatus,
    setBookingStatus
  } = useInitialVisitStore();

  const { data: initialVisitDetails } = useInitialVisitDetails({
    uniqueId: uniqueId,
    enabled: Boolean(uniqueId)
  });

  const {
    data: waitlistAvailability,
    isLoading: isLoadingWaitlistAvailability
  } = useWaitlistAvailability({
    uniqueId: uniqueId,
    enabled: Boolean(uniqueId)
  });

  useEffect(() => {
    if (waitlistAvailability?.dates) {
      const uniqueDates = Array.from(
        new Map(
          waitlistAvailability.dates.map((day: string) => {
            const date = DateTime.fromISO(day).toJSDate();
            return [date.getTime(), date];
          })
        ).values()
      ) as Date[];
      setSelectedDates(uniqueDates);
    }
  }, [waitlistAvailability]);

  const [waitlistLoading, setWaitlistLoading] = useState(false);

  const [selectedDates, setSelectedDates] = useState<Date[]>([]);

  const handleWaitlist = useCallback(async () => {
    setWaitlistLoading(true);

    try {
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/bookings/waitlist`,
        {
          uniqueId: uniqueId,
          appointmentTypeId: initialVisitDetails?.appointment.appointmentTypeId,
          dates: selectedDates.map((date) => date.toISOString()),
          type: "replace"
        }
      );
      setBookingStatus("waitlist");
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          "There was an error joining the waitlist. Please try again or contact the clinic for help."
      );
    } finally {
      setWaitlistLoading(false);
    }
  }, [uniqueId, selectedDates]);

  if (isLoadingWaitlistAvailability) {
    return (
      <main className="p-8 min-h-svh bg-accent dark:bg-background flex flex-col items-center justify-center">
        <Loader2 className="size-6 animate-spin text-primary" />
      </main>
    );
  }

  return (
    <main className="p-8 min-h-svh bg-accent dark:bg-background flex flex-col items-center justify-center">
      <Card className="w-full max-w-xl relative">
        <CardHeader className="gap-1">
          <CardTitle className="text-muted-foreground font-medium text-sm">
            {initialVisitDetails?.organization.name}
          </CardTitle>
          <CardDescription className="text-foreground header-3">
            Initial Examination
          </CardDescription>
          <CardAction className="text-xs tracking-wide uppercase text-muted-foreground font-medium">
            Waitlist
          </CardAction>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 flex flex-col items-center text-center border-t border-b py-6">
            <div className="space-y-0.5 max-w-md">
              <h2 className="text-base font-semibold">
                When are you available?
              </h2>
              <p className="text-sm text-muted-foreground">
                Select all dates that work for you. We’ll text you if a spot
                opens.
              </p>
            </div>
            <Calendar
              mode="multiple"
              required={false}
              showOutsideDays={false}
              selected={selectedDates}
              onSelect={(dates) => setSelectedDates(dates || [])}
              className="rounded-md border"
            />
          </div>
        </CardContent>
        <CardFooter className="flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => {
              setWaitlist(false);
              setDate(DateTime.now().startOf("day"));
              setVisibleMonth(DateTime.now().startOf("month"));
            }}
          >
            Back
          </Button>
          <Button disabled={waitlistLoading} onClick={handleWaitlist}>
            {waitlistLoading && <Loader2 className="size-4 animate-spin" />}
            Confirm
          </Button>
        </CardFooter>
      </Card>
    </main>
  );
}
